import React from "react";
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from "prop-types";
import cx from "classnames";

import Label from "../Label";
import Message from "../Message";

import SelectStatic from "./Select.static";

const CLASS_ROOT = "select";

const CHOICES_EVENTS = [
  "addItem",
  "removeItem",
  "highlightItem",
  "unhighlightItem",
  "choice",
  "change",
  "search",
  "showDropdown",
  "hideDropdown",
];

export default class Select extends React.Component {
  static displayName = "Select";

  static propTypes = {
    /** Choices options. (https://github.com/jshjohnson/Choices#configuration-options) */
    choicesOptions: object,
    /** Error state. If vaule is string, it's treated as error message. */
    error: oneOfType([bool, node]),
    /** Help text. */
    help: string,
    /** Html id attribute. */
    id: string.isRequired,
    /** Select can be cleared to default value. */
    isClearable: bool,
    /** Disabled state. */
    isDisabled: bool,
    /** Label text. */
    label: string,
    /** Html name attribute. */
    name: string,
    /** Value change callback function. */
    onChange: func,
    /** Select option can be specfied via children or as options array */
    options: arrayOf(
      oneOfType([shape({ text: string, value: string }), string])
    ),
    /** Placeholder text */
    placeholder: string,
    /** Custom error renderer. Passes select props as function parameter. */
    renderError: func,
    /** Custom help renderer. Passes select props as function parameter. */
    renderHelp: func,
    /** Custom label renderer. Passes select props as function parameter. */
    renderLabel: func,
    /** Size of element. */
    size: oneOf(["small", "large"]),
    /** Choices type. */
    type: oneOf(["text", "one", "multiple"]),
    /** Select value. */
    value: oneOfType([string, array]),
  };

  static defaultProps = {
    onChange: (newValue, e) => {}, // eslint-disable-line
    type: "one",
  };

  componentDidMount() {
    const { choicesOptions, value, type } = this.props;

    this.select = new SelectStatic(this.el, choicesOptions);

    if (value) {
      if (type !== "text") {
        this.select.instance.setChoiceByValue(value);
      } else {
        this.select.instance.setValue(value);
      }
    }

    CHOICES_EVENTS.forEach((eventName) =>
      this.el.addEventListener(eventName, this.handleEvent)
    );
  }

  // eslint-disable-next-line react/prop-types
  componentDidUpdate({ items, value }) {
    /* eslint-disable react/destructuring-assignment */
    if (items !== this.props.options) {
      this.select.instance.setChoices(
        this.props.options,
        "value",
        "label",
        true
      );
    }

    if (value !== this.props.value) {
      this.select.instance.setChoiceByValue(this.props.value);
    }
    /* eslint-enable react/destructuring-assignment */
  }

  componentWillUnmount() {
    CHOICES_EVENTS.forEach((eventName) =>
      this.el.removeEventListener(eventName, this.handleEvent)
    );

    this.select.destroy();
  }

  handleEvent = (e) => {
    const eventName = e.type.charAt(0).toUpperCase() + e.type.slice(1);
    // eslint-disable-next-line react/destructuring-assignment
    const eventHandler = this.props[`on${eventName}`];

    if (eventHandler) {
      eventHandler(e.detail, e);
    }
  };

  render() {
    const {
      className,
      children,
      error,
      renderError,
      label,
      renderLabel,
      help,
      renderHelp,
      options,
      placeholder,
      size,
      type,
      id,
      isDisabled,
      isClearable,
      onChange,
      value,
      ...other
    } = this.props;

    const classes = cx(
      CLASS_ROOT,
      {
        [`${CLASS_ROOT}--${size}`]: size,
        "is-error": error,
      },
      "is-hidden",
      className
    );

    // set name from id if name is not specfied
    let { name } = other;
    name = name && id;

    const formControlClasses = cx("form-control", "form-control--select");

    const selectLabel =
      (renderLabel && renderLabel(this.props)) ||
      (label && (
        <Label id={id} size={size} isError={!!error}>
          {label}
        </Label>
      ));

    const selectOptions =
      options &&
      options.map((option) => {
        const { value: optionValue, text, ...otherOptionProps } =
          typeof option === "string" ? { value: option, text: option } : option;
        return (
          <option value={optionValue} key={text} {...otherOptionProps}>
            {text}
          </option>
        );
      });

    const helpMessage =
      (renderHelp && renderHelp(this.props)) ||
      (help && <Message type="help">{help}</Message>);

    const errorMessage =
      (renderError && renderError(this.props)) ||
      (error && typeof error === "string" && (
        <Message type="error">{error}</Message>
      ));

    return (
      <div className={formControlClasses}>
        {selectLabel}
        <select
          multiple={type === "select-multiple"}
          id={id}
          name={name}
          className={classes}
          disabled={isDisabled}
          ref={(el) => {
            this.el = el;
          }}
          data-select=""
          data-select-clearable={isClearable}
          {...other}
          defaultValue={placeholder}
        >
          {selectOptions}
          {children}
          {placeholder && (
            <option disabled placeholder="">
              {placeholder}
            </option>
          )}
        </select>
        {helpMessage}
        {errorMessage}
      </div>
    );
  }
}
